import React, { useEffect, useMemo, useState } from 'react';
import { CalendarIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds } from '../../hooks/useApi';
import apiClient from '../../utils/api';

const ComplianceReport: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [selectedPondId, setSelectedPondId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const pondOptions = useMemo(() => ponds || [], [ponds]);

  useEffect(() => {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 6 * 86400000).toISOString().slice(0, 10);
    setStart(from);
    setEnd(to);
  }, []);

  const fetchData = async () => {
    if (!selectedLocation || !start || !end) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/reports/compliance', {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
        },
      });
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load compliance report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load data when filters/location ready (default last 7 days)
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, start, end, selectedPondId]);

  // Group rows by date and sort dates (recent first)
  const groupedByDate = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    for (const r of rows) {
      const key = r.date;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(r);
    }
    // Sort each group by pond name for consistency
    Object.keys(grouped).forEach((d) => {
      grouped[d].sort((a, b) => (a.pond_name || '').localeCompare(b.pond_name || ''));
    });
    return grouped;
  }, [rows]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  }, [groupedByDate]);

  const downloadCsv = async () => {
    if (!selectedLocation || !start || !end) return;
    try {
      const res = await apiClient.get(`/reports/export/compliance`, {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `compliance_${start}_${end}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="flex items-center gap-2">
        <CalendarIcon className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Daily Compliance</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Pond</label>
            <select value={selectedPondId} onChange={(e) => setSelectedPondId(e.target.value)} className="w-full px-3 py-2 border rounded text-sm">
              <option value="">All ponds</option>
              {pondOptions.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Apply Filters</button>
          <button onClick={downloadCsv} className="flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" /> CSV
          </button>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600">{error}</div>
        )}

        <div className="mt-4 space-y-4">
          {loading && (
            <div className="px-2 sm:px-3 py-3 text-sm">Loading...</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="px-2 sm:px-3 py-3 text-sm text-gray-500">No data for selected range</div>
          )}
          {!loading && sortedDates.map((date) => {
            const group = groupedByDate[date] || [];
            const totalMissing = group.reduce((sum, r) => sum + (Number(r.missing_count) || 0), 0);

            const byName = (names: string[]) => names.filter(Boolean).sort((a, b) => a.localeCompare(b));

            const feedNo = byName(group.filter((r) => !r.feed_ok).map((r) => r.pond_name));
            const feedYes = byName(group.filter((r) => !!r.feed_ok).map((r) => r.pond_name));

            const waterNo = byName(group.filter((r) => !r.water_quality_ok).map((r) => r.pond_name));
            const waterYes = byName(group.filter((r) => !!r.water_quality_ok).map((r) => r.pond_name));

            const medNo = byName(group.filter((r) => !r.medicine_present).map((r) => r.pond_name));
            const medYes = byName(group.filter((r) => !!r.medicine_present).map((r) => r.pond_name));

            const BadgeRow = ({ items, color }: { items: string[]; color: 'green' | 'red' }) => {
              if (items.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1">
                  {items.map((name) => (
                    <span
                      key={name}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        color === 'green' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              );
            };

            return (
              <div key={date} className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">{date}</div>
                  <div className="text-xs text-gray-600">Missing total: {totalMissing}</div>
                </div>
                <div className="p-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                  {/* Feed */}
                  <div className="bg-white border rounded-md p-2">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Feed</div>
                    <BadgeRow items={feedNo} color="red" />
                    <BadgeRow items={feedYes} color="green" />
                  </div>
                  {/* Water Quality */}
                  <div className="bg-white border rounded-md p-2">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Water Quality</div>
                    <BadgeRow items={waterNo} color="red" />
                    <BadgeRow items={waterYes} color="green" />
                  </div>
                  {/* Medicine */}
                  <div className="bg-white border rounded-md p-2">
                    <div className="text-sm font-semibold text-gray-800 mb-1">Medicine Present</div>
                    <BadgeRow items={medNo} color="red" />
                    <BadgeRow items={medYes} color="green" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ComplianceReport;


